import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCode, faLink, faArrowRight, faCopy, faCheck, faList, faShoppingCart, faUser } from '@fortawesome/free-solid-svg-icons';

const Documentation: React.FC = () => {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState<number>(1);

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(id);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const CodeBlock: React.FC<{ id: string; language: string; code: string }> = ({ id, language, code }) => (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => copyToClipboard(code, id)}
        style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          background: '#374151',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          padding: '4px 8px',
          fontSize: '12px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}
      >
        {copiedCode === id ? (
          <>
            <FontAwesomeIcon icon={faCheck} />
            Copied!
          </>
        ) : (
          <>
            <FontAwesomeIcon icon={faCopy} />
            Copy
          </>
        )}
      </button>
      <pre style={{
        background: '#1f2937',
        color: '#f9fafb',
        padding: '16px',
        borderRadius: '8px',
        overflow: 'auto',
        fontSize: '14px',
        lineHeight: '1.5',
        margin: 0
      }}>
        <code>{code}</code>
      </pre>
    </div>
  );

  const steps = [
    { id: 1, title: 'Overview', icon: faCode },
    { id: 2, title: 'List Plans', icon: faList },
    { id: 3, title: 'Generate Purchase URL', icon: faShoppingCart },
    { id: 4, title: 'Callback URL', icon: faLink },
    { id: 5, title: 'Check Feature Availability', icon: faCode },
    { id: 6, title: 'User Plan Details', icon: faUser },
    { id: 7, title: 'Sample Implementation', icon: faCode }
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Sidebar */}
      <div style={{ 
        width: '280px', 
        backgroundColor: 'white', 
        borderRight: '1px solid #e2e8f0',
        padding: '24px 0',
        position: 'fixed',
        height: '100vh',
        overflowY: 'auto'
      }}>
        <div style={{ padding: '0 24px', marginBottom: '32px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937', marginBottom: '8px' }}>
            <FontAwesomeIcon icon={faCode} style={{ marginRight: '8px', color: '#3b82f6' }} />
            ZeaOne Docs
          </h2>
          <p style={{ fontSize: '14px', color: '#6b7280' }}>
            Integration Guide
          </p>
        </div>
        
        <nav style={{ padding: '0 16px' }}>
          {steps.map((step) => (
            <button
              key={step.id}
              onClick={() => setActiveStep(step.id)}
              style={{
                width: '100%',
                padding: '12px 16px',
                marginBottom: '4px',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: activeStep === step.id ? '#3b82f6' : 'transparent',
                color: activeStep === step.id ? 'white' : '#374151',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontSize: '14px',
                fontWeight: '500',
                textAlign: 'left',
                transition: 'all 0.2s ease'
              }}
            >
              <FontAwesomeIcon 
                icon={step.icon} 
                style={{ 
                  fontSize: '16px',
                  color: activeStep === step.id ? 'white' : '#6b7280'
                }} 
              />
              {step.title}
            </button>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div style={{ 
        marginLeft: '280px', 
        flex: 1, 
        padding: '40px',
        maxWidth: 'calc(100vw - 280px)'
      }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', marginBottom: '8px' }}>
            {steps.find(s => s.id === activeStep)?.title}
          </h1>
          <p style={{ fontSize: '16px', color: '#6b7280' }}>
            {activeStep === 1 && 'Complete guide to integrate ZeaOne payment system into your application'}
            {activeStep === 2 && 'Learn how to fetch available plans for your application'}
            {activeStep === 3 && 'Generate purchase URLs for your users to complete payments'}
            {activeStep === 4 && 'Handle purchase completion notifications from ZeaOne'}
            {activeStep === 5 && 'Check if a user has access to specific features in their plan'}
            {activeStep === 6 && 'Get complete plan details and available features for a user'}
            {activeStep === 7 && 'Complete working examples for different programming languages'}
          </p>
        </div>

        {/* Step Content */}
        {activeStep === 1 && (
          <div style={{ 
            background: 'white', 
            padding: '32px', 
            borderRadius: '12px', 
            marginBottom: '32px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
              <FontAwesomeIcon icon={faCode} style={{ marginRight: '8px', color: '#3b82f6' }} />
              Integration Overview
            </h2>
            <p style={{ fontSize: '16px', color: '#374151', lineHeight: '1.6', marginBottom: '24px' }}>
              This documentation will guide you through integrating ZeaOne's payment system into your application. 
              The integration consists of five main steps:
            </p>
            
            <div style={{ display: 'grid', gap: '16px', marginBottom: '24px' }}>
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#f0f9ff', 
                borderRadius: '8px',
                border: '1px solid #bae6fd'
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#0369a1', marginBottom: '8px' }}>
                  <FontAwesomeIcon icon={faList} style={{ marginRight: '8px' }} />
                  1. List Plans
                </h3>
                <p style={{ fontSize: '14px', color: '#0c4a6e' }}>
                  Fetch available subscription plans for your application using your API key and App ID.
                </p>
              </div>
              
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#f0fdf4', 
                borderRadius: '8px',
                border: '1px solid #bbf7d0'
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#166534', marginBottom: '8px' }}>
                  <FontAwesomeIcon icon={faShoppingCart} style={{ marginRight: '8px' }} />
                  2. Generate Purchase URL
                </h3>
                <p style={{ fontSize: '14px', color: '#14532d' }}>
                  Create a purchase link for users to complete payment with their details and selected plan.
                </p>
              </div>
              
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#fef3c7', 
                borderRadius: '8px',
                border: '1px solid #fde68a'
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#92400e', marginBottom: '8px' }}>
                  <FontAwesomeIcon icon={faLink} style={{ marginRight: '8px' }} />
                  3. Handle Callback
                </h3>
                <p style={{ fontSize: '14px', color: '#78350f' }}>
                  Receive notifications when purchases are completed and update your system accordingly.
                </p>
              </div>
              
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#ecfdf5', 
                borderRadius: '8px',
                border: '1px solid #a7f3d0'
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#059669', marginBottom: '8px' }}>
                  <FontAwesomeIcon icon={faCode} style={{ marginRight: '8px' }} />
                  4. Check Feature Availability
                </h3>
                <p style={{ fontSize: '14px', color: '#047857' }}>
                  Verify if users have access to specific features in their purchased plans.
                </p>
              </div>
              
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#f3e8ff', 
                borderRadius: '8px',
                border: '1px solid #c4b5fd'
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#7c3aed', marginBottom: '8px' }}>
                  <FontAwesomeIcon icon={faCode} style={{ marginRight: '8px' }} />
                  5. Sample Implementation
                </h3>
                <p style={{ fontSize: '14px', color: '#5b21b6' }}>
                  Complete working examples for different programming languages and frameworks.
                </p>
              </div>
            </div>

            <div style={{ 
              padding: '20px', 
              backgroundColor: '#f8fafc', 
              borderRadius: '8px',
              border: '1px solid #e2e8f0'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Prerequisites
              </h3>
              <ul style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.6', paddingLeft: '20px' }}>
                <li>ZeaOne account with API access</li>
                <li>Application created in your ZeaOne dashboard</li>
                <li>API key generated for your application</li>
                <li>Plans configured for your application</li>
              </ul>
            </div>
          </div>
        )}

        {activeStep === 2 && (
          <div style={{ 
            background: 'white', 
            padding: '32px', 
            borderRadius: '12px', 
            marginBottom: '32px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
              <FontAwesomeIcon icon={faList} style={{ marginRight: '8px', color: '#3b82f6' }} />
              List Plans API
            </h2>
            <p style={{ fontSize: '16px', color: '#374151', lineHeight: '1.6', marginBottom: '24px' }}>
              Fetch all available subscription plans for your application. This endpoint requires your API key and returns 
              plan details including pricing, features, and availability.
            </p>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                API Endpoint
              </h3>
              <div style={{
                backgroundColor: '#f8fafc',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontFamily: 'monospace', fontSize: '14px', color: '#374151' }}>
                  GET {process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v1/client/applications/&lt;app_id&gt;/plans
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Headers
              </h3>
              <div style={{
                backgroundColor: '#f8fafc',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontFamily: 'monospace', fontSize: '14px', color: '#374151' }}>
                  <div>X-API-Key: <span style={{ color: '#6b7280' }}>your_api_key_here</span></div>
                  <div>Content-Type: <span style={{ color: '#6b7280' }}>application/json</span></div>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Example Request
              </h3>
              <CodeBlock
                id="request-1"
                language="bash"
                code={`curl -X GET "${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v1/client/applications/<app_id>/plans" \\
  -H "X-API-Key: your_api_key_here" \\
  -H "Content-Type: application/json"`}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Success Response
              </h3>
              <CodeBlock
                id="response-1"
                language="json"
                code={`{
  "plans": [
    {
      "id": "<plan_id>",
      "name": "<plan_name>",
      "description": "<plan_description>",
      "pricing_tiers": [
        {
          "duration": "<duration>",
          "amount": "<amount>",
          "currency": "<currency>"
        }
      ],
      "selected_features": ["<feature1>", "<feature2>"],
      "consumable_counts": {
        "<feature>": <count>
      },
      "is_active": <is_active>
    }
  ],
  "application": {
    "id": "<app_id>",
    "name": "<application_name>"
  }
}`}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Error Responses
              </h3>
              <div style={{ display: 'grid', gap: '12px' }}>
                <div style={{
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  padding: '16px'
                }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#dc2626', marginBottom: '8px' }}>
                    Missing API Key
                  </h4>
                  <CodeBlock
                    id="error-1"
                    language="json"
                    code={`{
  "error": "missing_api_key",
  "message": "API key is required",
  "details": "Please include your API key in the X-API-Key header"
}`}
                  />
                </div>
                <div style={{
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  padding: '16px'
                }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#dc2626', marginBottom: '8px' }}>
                    Invalid API Key
                  </h4>
                  <CodeBlock
                    id="error-2"
                    language="json"
                    code={`{
  "error": "invalid_api_key",
  "message": "Invalid API key",
  "details": "The provided API key is not valid or has been revoked"
}`}
                  />
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                JavaScript Example
              </h3>
              <CodeBlock
                id="plans-js-example"
                language="javascript"
                code={`const fetchPlans = async (appId, apiKey) => {
  const response = await fetch(\`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v1/client/applications/\${appId}/plans\`, {
    method: 'GET',
    headers: {
      'X-API-Key': apiKey,
      'Content-Type': 'application/json'
    }
  });
  
  const result = await response.json();
  
  if (response.ok) {
    console.log('Plans fetched successfully:', result.plans);
    return { success: true, plans: result.plans, application: result.application };
  } else {
    console.error('Failed to fetch plans:', result);
    return { success: false, error: result };
  }
};

// Usage
fetchPlans('app123', 'your_api_key_here');`}
              />
            </div>

            <div style={{ 
              padding: '16px', 
              backgroundColor: '#fef3c7', 
              borderRadius: '8px',
              border: '1px solid #f59e0b'
            }}>
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#92400e', marginBottom: '8px' }}>
                Important Notes
              </h4>
              <ul style={{ fontSize: '14px', color: '#92400e', margin: 0, paddingLeft: '20px' }}>
                <li>Only returns active plans (inactive plans are filtered out)</li>
                <li>Pricing tiers include all available durations (monthly, yearly, etc.)</li>
                <li>Selected features list shows all features included in the plan</li>
                <li>Consumable counts show the allocated amount for consumable features</li>
                <li>API key must be valid and associated with the application</li>
              </ul>
            </div>
          </div>
        )}

        {activeStep === 3 && (
          <div style={{ 
            background: 'white', 
            padding: '32px', 
            borderRadius: '12px', 
            marginBottom: '32px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
              <FontAwesomeIcon icon={faShoppingCart} style={{ marginRight: '8px', color: '#3b82f6' }} />
              Generate Purchase URL
            </h2>
            <p style={{ fontSize: '16px', color: '#374151', lineHeight: '1.6', marginBottom: '24px' }}>
              Create a purchase URL for users to complete their subscription payment. This endpoint generates a unique 
              transaction ID and returns a URL where users can complete their purchase.
            </p>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                API Endpoint
              </h3>
              <div style={{
                backgroundColor: '#f8fafc',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontFamily: 'monospace', fontSize: '14px', color: '#374151' }}>
                  POST {process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v1/purchases
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Headers
              </h3>
              <div style={{
                backgroundColor: '#f8fafc',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontFamily: 'monospace', fontSize: '14px', color: '#374151' }}>
                  <div>Authorization: <span style={{ color: '#6b7280' }}>Bearer your_token_here</span></div>
                  <div>Content-Type: <span style={{ color: '#6b7280' }}>application/json</span></div>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Example Request
              </h3>
              <CodeBlock
                id="purchase-request-example"
                language="bash"
                code={`curl -X POST "${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v1/purchases" \\
  -H "Authorization: Bearer your_token_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "user_id": "user123",
    "user_email": "user@example.com",
    "user_name": "John Doe",
    "plan_id": "plan456",
    "duration": "monthly",
    "webhook_url": "https://your-app.com/webhook",
    "redirect_url": "https://your-app.com/success"
  }'`}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Request Body
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
                All fields except <code style={{ backgroundColor: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}>user_name</code>, <code style={{ backgroundColor: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}>duration</code>, <code style={{ backgroundColor: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}>webhook_url</code>, and <code style={{ backgroundColor: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}>redirect_url</code> are required. If <code style={{ backgroundColor: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}>webhook_url</code> or <code style={{ backgroundColor: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}>redirect_url</code> are not provided, the application's configured URLs will be used.
              </p>
              <CodeBlock
                id="request-2"
                language="json"
                code={`{
  "user_id": "<user_id>",
  "user_email": "<user_email>",
  "user_name": "<user_name>",
  "plan_id": "<plan_id>",
  "duration": "<duration>",
  "webhook_url": "<webhook_url>",
  "redirect_url": "<redirect_url>"
}`}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Optional Parameters
              </h3>
              <div style={{
                backgroundColor: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#0369a1', marginBottom: '8px' }}>
                  webhook_url
                </h4>
                <p style={{ fontSize: '14px', color: '#0c4a6e', marginBottom: '12px' }}>
                  Optional. URL where purchase completion notifications will be sent. If not provided, the application's configured callback URL will be used.
                </p>
                <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#0369a1', marginBottom: '8px' }}>
                  redirect_url
                </h4>
                <p style={{ fontSize: '14px', color: '#0c4a6e', marginBottom: '0' }}>
                  Optional. URL where users will be redirected after completing or canceling the purchase. If not provided, the application's configured redirect URL will be used.
                </p>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Success Response
              </h3>
              <CodeBlock
                id="response-2"
                language="json"
                code={`{
  "id": "<purchase_id>",
  "transaction_id": "<transaction_id>",
  "user_email": "<user_email>",
  "user_name": "<user_name>",
  "plan_id": "<plan_id>",
  "application_id": "<app_id>",
  "amount": "<amount>",
  "currency": "<currency>",
  "duration": "<duration>",
  "start_date": "<start_date>",
  "end_date": "<end_date>",
  "status": "<status>",
  "callback_sent": "<callback_sent>",
  "created_at": "<created_at>",
  "updated_at": "<updated_at>"
}`}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Purchase URL
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
                Use the returned transaction_id to construct the purchase URL:
              </p>
              <div style={{
                backgroundColor: '#f8fafc',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                fontFamily: 'monospace',
                fontSize: '14px',
                color: '#374151'
              }}>
                {process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v1/purchase/&lt;transaction_id&gt;
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Error Responses
              </h3>
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '16px'
              }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#dc2626', marginBottom: '8px' }}>
                  Active Subscription Exists
                </h4>
                <CodeBlock
                  id="error-3"
                  language="json"
                  code={`{
  "error": "active_subscription_exists",
  "message": "Active subscription already exists",
  "details": "You already have an active <duration> subscription. Please wait for it to expire or cancel it before purchasing a new one."
}`}
                />
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                JavaScript Example
              </h3>
              <CodeBlock
                id="purchase-js-example"
                language="javascript"
                code={`const generatePurchaseURL = async (userId, userEmail, userName, planId, duration, token, webhookUrl, redirectUrl) => {
  const response = await fetch('${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v1/purchases', {
    method: 'POST',
    headers: {
      'Authorization': \`Bearer \${token}\`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      user_id: userId,
      user_email: userEmail,
      user_name: userName,
      plan_id: planId,
      duration: duration,
      webhook_url: webhookUrl,  // Optional: overrides application callback_url
      redirect_url: redirectUrl  // Optional: overrides application redirect_url
    })
  });
  
  const result = await response.json();
  
  if (response.ok) {
    const purchaseUrl = \`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v1/purchase/\${result.transaction_id}\`;
    console.log('Purchase URL generated:', purchaseUrl);
    return { success: true, purchaseUrl, transactionId: result.transaction_id };
  } else {
    console.error('Failed to generate purchase URL:', result);
    return { success: false, error: result };
  }
};

// Usage
generatePurchaseURL('user123', 'user@example.com', 'John Doe', 'plan456', 'monthly', 'your_token');`}
              />
            </div>

            <div style={{ 
              padding: '16px', 
              backgroundColor: '#fef3c7', 
              borderRadius: '8px',
              border: '1px solid #f59e0b'
            }}>
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#92400e', marginBottom: '8px' }}>
                Important Notes
              </h4>
              <ul style={{ fontSize: '14px', color: '#92400e', margin: 0, paddingLeft: '20px' }}>
                <li>Purchase URLs are valid for 24 hours after generation</li>
                <li>Each transaction_id can only be used once</li>
                <li>Users cannot have multiple active subscriptions for the same plan</li>
                <li>Callback URL will be triggered upon successful payment completion</li>
                <li>All amounts are processed in the plan's specified currency</li>
              </ul>
            </div>
          </div>
        )}

        {activeStep === 4 && (
          <div style={{ 
            background: 'white', 
            padding: '32px', 
            borderRadius: '12px', 
            marginBottom: '32px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
              <FontAwesomeIcon icon={faLink} style={{ marginRight: '8px', color: '#3b82f6' }} />
              Callback URL
            </h2>
            <p style={{ fontSize: '16px', color: '#374151', lineHeight: '1.6', marginBottom: '24px' }}>
              When a purchase is completed, ZeaOne will send a POST request to your configured callback URL with 
              the purchase details. This allows you to update your system and grant access to the user.
            </p>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Callback Request
              </h3>
              <div style={{
                backgroundColor: '#f8fafc',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontFamily: 'monospace', fontSize: '14px', color: '#374151' }}>
                  <div>Method: <span style={{ color: '#6b7280' }}>POST</span></div>
                  <div>URL: <span style={{ color: '#6b7280' }}>your_callback_url</span></div>
                  <div>Content-Type: <span style={{ color: '#6b7280' }}>application/json</span></div>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Callback Payload
              </h3>
              <CodeBlock
                id="callback-1"
                language="json"
                code={`{
  "transaction_id": "<transaction_id>",
  "user_id": "<user_id>",
  "user_email": "<user_email>",
  "user_name": "<user_name>",
  "plan_id": "<plan_id>",
  "plan_code": "<plan_code>",
  "application_id": "<app_id>",
  "amount": "<amount>",
  "currency": "<currency>",
  "duration": "<duration>",
  "start_date": "<start_date>",
  "end_date": "<end_date>",
  "status": "<status>",
  "application_name": "<application_name>"
}`}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Expected Response
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
                Your callback endpoint should return a 200 status code to acknowledge receipt:
              </p>
              <CodeBlock
                id="callback-response"
                language="json"
                code={`{
  "status": "received",
  "message": "Callback processed successfully"
}`}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Configuration
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
                Set your callback URL in the ZeaOne dashboard under your application settings:
              </p>
              <div style={{
                backgroundColor: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: '8px',
                padding: '16px'
              }}>
                <div style={{ fontFamily: 'monospace', fontSize: '14px', color: '#0369a1' }}>
                  https://yourdomain.com/api/callback/zeabill
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Security Considerations
              </h3>
              <ul style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.6', paddingLeft: '20px' }}>
                <li>Verify the callback is from ZeaOne (check IP or use webhook signatures)</li>
                <li>Validate the transaction_id exists in your system</li>
                <li>Check the status field to ensure the purchase was successful</li>
                <li>Implement idempotency to handle duplicate callbacks</li>
                <li>Log all callback attempts for debugging</li>
              </ul>
            </div>
          </div>
        )}

        {activeStep === 5 && (
          <div style={{ 
            background: 'white', 
            padding: '32px', 
            borderRadius: '12px', 
            marginBottom: '32px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
              <FontAwesomeIcon icon={faCode} style={{ marginRight: '8px', color: '#3b82f6' }} />
              Check Feature Availability
            </h2>
            <p style={{ fontSize: '16px', color: '#374151', lineHeight: '1.6', marginBottom: '24px' }}>
              Check if a user has access to specific features in their purchased plan. This API returns 
              <code style={{ backgroundColor: '#f3f4f6', padding: '2px 6px', borderRadius: '4px', fontSize: '14px' }}>true</code> for 
              non-consumable features and the available count for consumable features.
            </p>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                API Endpoint
              </h3>
              <div style={{
                backgroundColor: '#f8fafc',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontFamily: 'monospace', fontSize: '14px', color: '#374151' }}>
                  POST {process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v1/client/applications/&lt;app_id&gt;/feature-availability
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Headers
              </h3>
              <div style={{
                backgroundColor: '#f8fafc',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontFamily: 'monospace', fontSize: '14px', color: '#374151' }}>
                  <div>X-API-Key: <span style={{ color: '#6b7280' }}>your_api_key_here</span></div>
                  <div>Content-Type: <span style={{ color: '#6b7280' }}>application/json</span></div>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Example Request
              </h3>
              <CodeBlock
                id="feature-request-example"
                language="bash"
                code={`curl -X POST "${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v1/client/applications/<app_id>/feature-availability" \\
  -H "X-API-Key: your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "user_id": "user123",
    "feature_code": "USERS"
  }'`}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Request Body
              </h3>
              <CodeBlock
                id="feature-request"
                language="json"
                code={`{
  "user_id": "<user_id>",
  "feature_code": "<feature_code>"
}`}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Success Response
              </h3>
              
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                  Non-consumable feature (available)
                </h4>
                <CodeBlock
                  id="feature-response-1"
                  language="json"
                  code={`{
  "available": true,
  "count": null,
  "feature_code": "premium_support",
  "user_id": "user123",
  "app_id": "app456"
}`}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                  Consumable feature (with count)
                </h4>
                <CodeBlock
                  id="feature-response-2"
                  language="json"
                  code={`{
  "available": true,
  "count": 100,
  "feature_code": "api_calls",
  "user_id": "user123",
  "app_id": "app456"
}`}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                  Feature not available
                </h4>
                <CodeBlock
                  id="feature-response-3"
                  language="json"
                  code={`{
  "available": false,
  "count": 0,
  "feature_code": "advanced_analytics",
  "user_id": "user123",
  "app_id": "app456"
}`}
                />
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                JavaScript Example
              </h3>
              <CodeBlock
                id="feature-js-example"
                language="javascript"
                code={`const checkFeatureAvailability = async (userId, appId, featureCode, apiKey) => {
  const response = await fetch(\`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v1/client/applications/\${appId}/feature-availability\`, {
    method: 'POST',
    headers: {
      'X-API-Key': apiKey,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      user_id: userId,
      feature_code: featureCode
    })
  });
  
  const result = await response.json();
  
  if (result.available) {
    if (result.count !== null) {
      console.log(\`User has \${result.count} \${featureCode} available\`);
    } else {
      console.log(\`User has access to \${featureCode}\`);
    }
  } else {
    console.log(\`User does not have access to \${featureCode}\`);
  }
  
  return result;
};

// Usage
checkFeatureAvailability('user123', 'app456', 'api_calls', 'your_api_key');`}
              />
            </div>

            <div style={{ 
              padding: '16px', 
              backgroundColor: '#fef3c7', 
              borderRadius: '8px',
              border: '1px solid #f59e0b'
            }}>
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#92400e', marginBottom: '8px' }}>
                Important Notes
              </h4>
              <ul style={{ fontSize: '14px', color: '#92400e', margin: 0, paddingLeft: '20px' }}>
                <li>Only checks active subscriptions (not expired)</li>
                <li>Returns the most recent purchase for the user</li>
                <li>Uses plan snapshot data for accurate feature checking</li>
                <li>Returns <code>false</code> and <code>count: 0</code> if user has no active subscription</li>
              </ul>
            </div>
          </div>
        )}

        {activeStep === 6 && (
          <div style={{ 
            background: 'white', 
            padding: '32px', 
            borderRadius: '12px', 
            marginBottom: '32px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
              <FontAwesomeIcon icon={faUser} style={{ marginRight: '8px', color: '#3b82f6' }} />
              User Plan Details
            </h2>
            <p style={{ fontSize: '16px', color: '#374151', lineHeight: '1.6', marginBottom: '24px' }}>
              Get complete details about a user's currently active subscription plan. This API returns the plan name, 
              duration, available features, consumable limits, and subscription dates for the user's active plan.
            </p>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                API Endpoint
              </h3>
              <div style={{
                backgroundColor: '#f8fafc',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontFamily: 'monospace', fontSize: '14px', color: '#374151' }}>
                  POST {process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v1/client/applications/&lt;app_id&gt;/user-plan-details
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Headers
              </h3>
              <div style={{
                backgroundColor: '#f8fafc',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontFamily: 'monospace', fontSize: '14px', color: '#374151' }}>
                  <div>X-API-Key: <span style={{ color: '#6b7280' }}>your_api_key_here</span></div>
                  <div>Content-Type: <span style={{ color: '#6b7280' }}>application/json</span></div>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Example Request
              </h3>
              <CodeBlock
                id="user-plan-request-example"
                language="bash"
                code={`curl -X POST "${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v1/client/applications/<app_id>/user-plan-details" \\
  -H "X-API-Key: your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "user_id": "user123"
  }'`}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Request Body
              </h3>
              <CodeBlock
                id="user-plan-request"
                language="json"
                code={`{
  "user_id": "<user_id>"
}`}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Success Response
              </h3>
              
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                  User with active plan
                </h4>
                <CodeBlock
                  id="user-plan-response-1"
                  language="json"
                  code={`{
  "has_active_plan": true,
  "plan_name": "Premium Plan",
  "plan_description": "Access to all premium features",
  "duration": "monthly",
  "start_date": "2024-01-01T00:00:00Z",
  "end_date": "2024-02-01T00:00:00Z",
  "available_features": [
    "premium_support",
    "advanced_analytics",
    "api_calls",
    "file_uploads"
  ],
  "consumable_limits": {
    "api_calls": 10000,
    "file_uploads": 100
  },
  "user_id": "user123",
  "app_id": "app456"
}`}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                  User without active plan
                </h4>
                <CodeBlock
                  id="user-plan-response-2"
                  language="json"
                  code={`{
  "has_active_plan": false,
  "plan_name": null,
  "plan_description": null,
  "duration": null,
  "start_date": null,
  "end_date": null,
  "available_features": [],
  "consumable_limits": {},
  "user_id": "user123",
  "app_id": "app456"
}`}
                />
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                JavaScript Example
              </h3>
              <CodeBlock
                id="user-plan-js-example"
                language="javascript"
                code={`const getUserPlanDetails = async (userId, appId, apiKey) => {
  const response = await fetch(\`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v1/client/applications/\${appId}/user-plan-details\`, {
    method: 'POST',
    headers: {
      'X-API-Key': apiKey,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      user_id: userId
    })
  });
  
  const result = await response.json();
  
  if (result.has_active_plan) {
    console.log(\`User has active plan: \${result.plan_name}\`);
    console.log(\`Available features: \${result.available_features.join(', ')}\`);
    console.log(\`Consumable limits: \${JSON.stringify(result.consumable_limits)}\`);
    console.log(\`Plan duration: \${result.duration}\`);
    console.log(\`Valid until: \${result.end_date}\`);
  } else {
    console.log('User has no active plan');
  }
  
  return result;
};

// Usage
getUserPlanDetails('user123', 'app456', 'your_api_key');`}
              />
            </div>

            <div style={{ 
              padding: '16px', 
              backgroundColor: '#fef3c7', 
              borderRadius: '8px',
              border: '1px solid #f59e0b'
            }}>
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#92400e', marginBottom: '8px' }}>
                Important Notes
              </h4>
              <ul style={{ fontSize: '14px', color: '#92400e', margin: 0, paddingLeft: '20px' }}>
                <li>Only returns data for active subscriptions (not expired)</li>
                <li>Returns the most recent purchase for the user</li>
                <li>Uses plan snapshot data for accurate feature details</li>
                <li>Consumable limits show the total allocated amount for each feature</li>
                <li>Returns empty data if user has no active subscription</li>
              </ul>
            </div>
          </div>
        )}

        {activeStep === 7 && (
          <div style={{ 
            background: 'white', 
            padding: '32px', 
            borderRadius: '12px', 
            marginBottom: '32px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
              <FontAwesomeIcon icon={faCode} style={{ marginRight: '8px', color: '#3b82f6' }} />
              Sample Implementation
            </h2>
            <p style={{ fontSize: '16px', color: '#374151', lineHeight: '1.6', marginBottom: '24px' }}>
              Complete working examples for different programming languages and frameworks. These examples show 
              the full integration flow from listing plans to handling callbacks.
            </p>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                JavaScript/Node.js
              </h3>
              <CodeBlock
                id="js-example"
                language="javascript"
                code={`// List plans
const listPlans = async (appId, apiKey) => {
  const response = await fetch(\`http://localhost:8001/api/v1/client/applications/\${appId}/plans\`, {
    headers: {
      'X-API-Key': apiKey,
      'Content-Type': 'application/json'
    }
  });
  return await response.json();
};

// Generate purchase URL
const createPurchase = async (userData, planId, duration) => {
  const response = await fetch('http://localhost:8001/api/v1/purchases', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      user_id: userData.userId,
      user_email: userData.email,
      user_name: userData.name,
      plan_id: planId,
      duration: duration
    })
  });
  return await response.json();
};

// Handle callback
app.post('/api/callback/zeabill', (req, res) => {
  const { transaction_id, status, user_id, plan_id } = req.body;
  
  if (status === 'completed') {
    // Grant access to user
    console.log(\`User \${user_id} purchased plan \${plan_id}\`);
  }
  
  res.json({ status: 'received' });
});`}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Python/Flask
              </h3>
              <CodeBlock
                id="python-example"
                language="python"
                code={`import requests
from flask import Flask, request, jsonify

# List plans
def list_plans(app_id, api_key):
    response = requests.get(
        f'http://localhost:8001/api/v1/client/applications/{app_id}/plans',
        headers={'X-API-Key': api_key}
    )
    return response.json()

# Generate purchase URL
def create_purchase(user_data, plan_id, duration):
    response = requests.post(
        'http://localhost:8001/api/v1/purchases',
        json={
            'user_id': user_data['user_id'],
            'user_email': user_data['email'],
            'user_name': user_data['name'],
            'plan_id': plan_id,
            'duration': duration
        }
    )
    return response.json()

# Handle callback
app = Flask(__name__)

@app.route('/api/callback/zeabill', methods=['POST'])
def handle_callback():
    data = request.json
    transaction_id = data.get('transaction_id')
    status = data.get('status')
    user_id = data.get('user_id')
    plan_id = data.get('plan_id')
    
    if status == 'completed':
        # Grant access to user
        print(f'User {user_id} purchased plan {plan_id}')
    
    return jsonify({'status': 'received'})`}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                PHP
              </h3>
              <CodeBlock
                id="php-example"
                language="php"
                code={`<?php
// List plans
function listPlans($appId, $apiKey) {
    $ch = curl_init();
    curl_setopt($ch, CURLOPT_URL, "http://localhost:8001/api/v1/client/applications/{$appId}/plans");
    curl_setopt($ch, CURLOPT_HTTPHEADER, [
        'X-API-Key: ' . $apiKey,
        'Content-Type: application/json'
    ]);
    curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
    $response = curl_exec($ch);
    curl_close($ch);
    return json_decode($response, true);
}

// Generate purchase URL
function createPurchase($userData, $planId, $duration) {
    $ch = curl_init();
    curl_setopt($ch, CURLOPT_URL, 'http://localhost:8001/api/v1/purchases');
    curl_setopt($ch, CURLOPT_POST, true);
    curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode([
        'user_id' => $userData['user_id'],
        'user_email' => $userData['email'],
        'user_name' => $userData['name'],
        'plan_id' => $planId,
        'duration' => $duration
    ]));
    curl_setopt($ch, CURLOPT_HTTPHEADER, ['Content-Type: application/json']);
    curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
    $response = curl_exec($ch);
    curl_close($ch);
    return json_decode($response, true);
}

// Handle callback
if ($_SERVER['REQUEST_METHOD'] === 'POST') {
    $data = json_decode(file_get_contents('php://input'), true);
    $transactionId = $data['transaction_id'];
    $status = $data['status'];
    $userId = $data['user_id'];
    $planId = $data['plan_id'];
    
    if ($status === 'completed') {
        // Grant access to user
        error_log("User {$userId} purchased plan {$planId}");
    }
    
    header('Content-Type: application/json');
    echo json_encode(['status' => 'received']);
}
?>`}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Testing
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
                Use the sample client application to test your integration:
              </p>
              <div style={{
                backgroundColor: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: '8px',
                padding: '16px'
              }}>
                <div style={{ fontFamily: 'monospace', fontSize: '14px', color: '#0369a1' }}>
                  http://localhost:3003
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Documentation;